import { TypedDocumentNode, gql } from '@apollo/client';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelXS } from '@sorare/core/src/atoms/typography';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { MultipleMatchupItem } from 'components/benchObject/MultipleMatchupItem';
import { SingleMatchupItem } from 'components/benchObject/SingleMatchupItem';

import { CalendarTooltip } from './CalendarTooltip';
import { BenchObjectUpcomingGames_anyPlayer } from './__generated__/index.graphql';

type Props = {
  anyPlayer: BenchObjectUpcomingGames_anyPlayer;
};

const Root = styled(Horizontal)`
  background-color: var(--current-row-color);
  padding: 0 var(--unit);
  display: none;
  @media ${laptopAndAbove} {
    display: flex;
    background-color: var(--current-row-color);
    padding: 0 var(--unit);
  }
`;
const RelativeWrapper = styled(Vertical).attrs({ gap: 0.5 })`
  position: relative;
  isolation: isolate;
`;
const EmptyWrapper = styled(Horizontal).attrs({ center: true })`
  height: var(--triple-unit);
  max-width: 120px;
  width: 100%;
`;

const Matchup = styled(Horizontal).attrs({ gap: 0 })`
  height: var(--triple-unit);
  min-width: 120px;
  border-radius: var(--half-unit);
  background: var(--c-nd-50);
  overflow: hidden;
`;

const Absolute = styled(Horizontal)`
  position: absolute;
  top: calc(100% + var(--half-unit));
  left: 0;
  right: 0;
`;

export const BenchObjectUpcomingGames = ({ anyPlayer }: Props) => {
  const { anyGamesForFixture } = anyPlayer;
  const hasMultipleGames = (anyGamesForFixture?.length || 0) > 1;
  const hasAGame = anyGamesForFixture?.length === 1;
  const nextGame = anyGamesForFixture?.[0];
  const fixtureName = nextGame?.so5Fixture?.shortDisplayName;

  return (
    <Root gap={0.5} center>
      {hasMultipleGames || hasAGame ? (
        <RelativeWrapper>
          <Matchup>
            {hasAGame && (
              <SingleMatchupItem
                anyGame={anyGamesForFixture[0]}
                playerTeamSlug={anyPlayer.activeClub?.slug}
              />
            )}
            {hasMultipleGames &&
              anyGamesForFixture?.map(anyGame => (
                <MultipleMatchupItem
                  key={anyGame.id}
                  anyGame={anyGame}
                  playerTeamSlug={anyPlayer?.activeClub?.slug}
                />
              ))}
          </Matchup>
          {fixtureName && (
            <Absolute center>
              <LabelXS color="var(--c-nd-600)">{fixtureName}</LabelXS>
            </Absolute>
          )}
        </RelativeWrapper>
      ) : (
        <EmptyWrapper>
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            color="var(--c-yellow-400)"
            width={10}
          />
          <LabelXS className="text-center">
            <FormattedMessage
              id="BenchObjectUpcomingGames.noGame"
              defaultMessage="No game next GW"
            />
          </LabelXS>
        </EmptyWrapper>
      )}
      <CalendarTooltip anyPlayer={anyPlayer} hasAGame={!!anyPlayer.nextGame} />
    </Root>
  );
};

BenchObjectUpcomingGames.fragments = {
  anyPlayer: gql`
    fragment BenchObjectUpcomingGames_anyPlayer on AnyPlayerInterface {
      slug
      activeClub {
        slug
      }
      nextGame {
        id
      }
      anyGamesForFixture(so5FixtureSlug: $so5FixtureSlug)
        @skip(if: $withoutSo5Fixture) {
        id
        so5Fixture {
          slug
          shortDisplayName
        }
        ...SingleMatchupItem_anyGame
        ...MultipleMatchupItem_anyGame
      }
      ...CalendarTooltip_anyPlayer
    }
    ${SingleMatchupItem.fragments.anyGame}
    ${MultipleMatchupItem.fragments.anyGame}
    ${CalendarTooltip.fragments.anyPlayer}
  ` as TypedDocumentNode<BenchObjectUpcomingGames_anyPlayer>,
};
